import React, { useEffect, useState } from "react";
import styled from "styled-components";

/* ===== API base ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

const api = {
  list: () => http(`/api/blog-topics`),
  remove: (id) => http(`/api/blog-topics/${id}`, { method: "DELETE" }),
};

const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  text: "#f1f1f1",
  subtext: "#d6d9de",
  bg: "#0b1320",
  card: "#0f1b2d",
  border: "#1e293b",
};

const Page = styled.div`display: grid; gap: 16px; color: ${theme.text};`;
const Title = styled.h2`margin: 0; font-weight: 800;`;
const Row = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap;`;
const Card = styled.div`background: ${theme.card}; border: 1px solid ${theme.border}; border-radius: 14px; overflow: hidden;`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; color: ${theme.text};
  th, td { border-bottom: 1px solid ${theme.border}; padding: 12px; text-align: left; vertical-align: top; }
  th { background: rgba(255,255,255,0.03); }
`;
const Danger = styled.button`
  background: #b91c1c; color: #fff; border: 0; padding: 8px 12px; border-radius: 8px; font-weight: 700; cursor: pointer;
  &:hover { background: #991b1b; }
`;
const TopicCell = styled.div`
  max-width: 560px;
  white-space: pre-wrap;
`;

export default function BlogTopicSuggestions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await api.list();
      setRows(res.data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function fmt(d) {
    return d
      ? new Date(d).toLocaleString(undefined, {
          year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        })
      : "";
  }

  async function handleDelete(row) {
    if (!window.confirm("Delete this topic suggestion?")) return;
    try {
      await api.remove(row._id);
      await load();
    } catch (e) {
      alert(e.message || "Failed to delete");
    }
  }

  return (
    <Page>
      <Row>
        <Title>Blog Topic Suggestions</Title>
      </Row>

      <Card>
        <Table>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Topic</th>
              <th>Submitted</th>
              <th style={{ width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} style={{ padding: 20 }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={4} style={{ padding: 20 }}>No topic suggestions yet.</td></tr>}
            {!loading && rows.map((r, idx) => (
              <tr key={r._id}>
                <td>{idx + 1}</td>
                <td><TopicCell>{r.topic}</TopicCell></td>
                <td>{fmt(r.createdAt)}</td>
                <td><Danger onClick={() => handleDelete(r)}>Delete</Danger></td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </Page>
  );
}
